/**
 * Minimal cron utility supporting the patterns actually in use:
 *   - "<min> <hour> * * <dow_list>" e.g. "0 2 * * 0,3" (Sun+Wed) or "0 20 * * 2,6" (Tue+Sat)
 *
 * Day-of-week semantics: 0=Sun, 1=Mon, ..., 6=Sat (standard cron).
 *
 * Not implemented: ranges (3-5), step values (* / 2), name aliases (MON, JAN),
 * day-of-month rules, multi-month rules. The schedules in
 * prod.data_collection_schedules use only the supported subset; we throw
 * `UnsupportedCronError` if we encounter anything richer so callers can fall
 * back to constants instead of silently miscomputing.
 *
 * Time zones are resolved via Intl.DateTimeFormat — no extra dependency.
 */

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const FALLBACK_SLO_HOURS = 108;

export class UnsupportedCronError extends Error {
  constructor(expression: string, reason: string) {
    super(`Unsupported cron expression "${expression}": ${reason}`);
    this.name = 'UnsupportedCronError';
  }
}

interface ParsedCron {
  minute: number;
  hour: number;
  daysOfWeek: number[];
}

function parseCron(expr: string): ParsedCron {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) {
    throw new UnsupportedCronError(expr, 'expected 5 fields');
  }
  const minStr = parts[0]!;
  const hrStr = parts[1]!;
  const dom = parts[2]!;
  const month = parts[3]!;
  const dowStr = parts[4]!;
  if (dom !== '*' || month !== '*') {
    throw new UnsupportedCronError(expr, 'day-of-month / month must be *');
  }
  const minute = Number(minStr);
  const hour = Number(hrStr);
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
    throw new UnsupportedCronError(expr, 'invalid minute');
  }
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    throw new UnsupportedCronError(expr, 'invalid hour');
  }
  const daysOfWeek = dowStr.split(',').map(s => Number(s));
  if (daysOfWeek.some(d => !Number.isInteger(d) || d < 0 || d > 6)) {
    throw new UnsupportedCronError(expr, 'invalid day-of-week list');
  }
  return { minute, hour, daysOfWeek: [...new Set(daysOfWeek)].sort((a, b) => a - b) };
}

/**
 * Return the timezone offset in minutes for a given instant + IANA tz name.
 * Used to convert wall-clock time in `tz` to a UTC instant.
 */
function tzOffsetMinutes(at: Date, timeZone: string): number {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const parts = fmt.formatToParts(at);
  const get = (t: string) => Number(parts.find(p => p.type === t)?.value);
  const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return (asUTC - at.getTime()) / 60_000;
}

/**
 * Convert a wall-clock (year, month, day, hour, minute) in `timeZone` to a UTC
 * Date. Iterates twice to handle DST transitions correctly.
 */
function wallClockToUTC(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  const guess = new Date(Date.UTC(year, month, day, hour, minute));
  const offset1 = tzOffsetMinutes(guess, timeZone);
  const corrected = new Date(guess.getTime() - offset1 * 60_000);
  const offset2 = tzOffsetMinutes(corrected, timeZone);
  return new Date(guess.getTime() - offset2 * 60_000);
}

function partsInTz(at: Date, timeZone: string) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
  const parts = fmt.formatToParts(at);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
  const weekdayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    weekday: weekdayMap[get('weekday')] ?? 0,
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute'))
  };
}

export function nextFire(expression: string, timeZone: string, from: Date = new Date()): Date | null {
  let parsed: ParsedCron;
  try {
    parsed = parseCron(expression);
  } catch {
    return null;
  }
  if (parsed.daysOfWeek.length === 0) return null;

  for (let dayOffset = 0; dayOffset <= 7; dayOffset++) {
    const candidateDate = new Date(from.getTime() + dayOffset * DAY_MS);
    const { weekday, year, month, day } = partsInTz(candidateDate, timeZone);
    if (!parsed.daysOfWeek.includes(weekday)) continue;
    const candidate = wallClockToUTC(year, month - 1, day, parsed.hour, parsed.minute, timeZone);
    if (candidate.getTime() > from.getTime()) return candidate;
  }
  return null;
}

/**
 * Maximum gap (in hours) between consecutive fires of a weekly cron.
 * For [0, 3] (Sun, Wed): gaps are 3 days (Sun→Wed) and 4 days (Wed→Sun) → 96h.
 * For [2, 6] (Tue, Sat): gaps are 4 (Tue→Sat) and 3 (Sat→Tue) → 96h.
 * Used to derive the freshness SLO with a grace buffer.
 */
export function maxGapHours(expression: string): number {
  let parsed: ParsedCron;
  try {
    parsed = parseCron(expression);
  } catch {
    return FALLBACK_SLO_HOURS - 12;
  }
  const days = parsed.daysOfWeek;
  if (days.length === 0) return FALLBACK_SLO_HOURS - 12;
  if (days.length === 1) return 7 * 24;
  let maxGap = 0;
  for (let i = 0; i < days.length; i++) {
    const next = days[(i + 1) % days.length] ?? 0;
    const cur = days[i] ?? 0;
    const gap = next > cur ? next - cur : 7 - cur + next;
    if (gap > maxGap) maxGap = gap;
  }
  return maxGap * 24;
}

export function sloHours(expression: string): number {
  return maxGapHours(expression) + 12;
}

/**
 * Best-effort human-readable for the cron form we actually use.
 * Falls back to the raw expression for anything we don't recognize.
 */
export function describeCron(expression: string, timeZone: string): string {
  let parsed: ParsedCron;
  try {
    parsed = parseCron(expression);
  } catch {
    return expression;
  }
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = parsed.daysOfWeek.map(d => dayNames[d]).join(' + ');
  const hh = String(parsed.hour).padStart(2, '0');
  const mm = String(parsed.minute).padStart(2, '0');
  const tzAbbr = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'short'
  })
    .formatToParts(new Date())
    .find(p => p.type === 'timeZoneName')?.value ?? timeZone;
  return `${days} at ${hh}:${mm} ${tzAbbr}`;
}

export const FALLBACK_SCHEDULE_DESCRIPTION = 'Wed + Sun (default)';
export const FALLBACK_SLO_HOURS_CONST = FALLBACK_SLO_HOURS;
